import forge from 'node-forge';

/**
 * Parse PEM/DER private key file and extract key components
 * Supports RSA and ECC keys
 */

// Convert hex string to byte array
export function hexToBytes(hex) {
  const bytes = [];
  for (let i = 0; i < hex.length; i += 2) {
    bytes.push(parseInt(hex.substr(i, 2), 16));
  }
  return bytes;
}

// Convert byte array to hex string
export function bytesToHex(bytes) {
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

// Convert BigInteger to hex string with proper padding
function bigIntToHex(bigInt, minLength) {
  let hex = bigInt.toString(16);
  if (hex.length % 2 !== 0) {
    hex = '0' + hex;
  }
  while (minLength && hex.length < minLength * 2) {
    hex = '00' + hex;
  }
  return hex;
}

/**
 * Parse PEM format private key
 * @param {string} pemContent - PEM file content
 * @returns {Object} Parsed key data
 */
export async function parsePEMPrivateKey(pemContent) {
  try {
    // Try to parse as RSA key first
    if (pemContent.includes('RSA PRIVATE KEY') || pemContent.includes('PRIVATE KEY')) {
      const key = forge.pki.privateKeyFromPem(pemContent);

      if (key.n) {
        // RSA key
        const bitLength = key.n.bitLength();
        const byteLength = Math.ceil(bitLength / 8);

        return {
          type: 'RSA',
          algorithm: bitLength === 2048 ? '07' : bitLength === 3072 ? '05' : bitLength === 4096 ? '16' : '07',
          size: bitLength,
          n: bigIntToHex(key.n, byteLength),
          e: bigIntToHex(key.e, 4),
          p: bigIntToHex(key.p, byteLength / 2),
          q: bigIntToHex(key.q, byteLength / 2),
          dP: bigIntToHex(key.dP, byteLength / 2),
          dQ: bigIntToHex(key.dQ, byteLength / 2),
          qInv: bigIntToHex(key.qInv, byteLength / 2)
        };
      }
    }

    // Try to parse as ECC key
    if (pemContent.includes('EC PRIVATE KEY')) {
      const key = forge.pki.privateKeyFromPem(pemContent);

      // Extract curve and private key
      const asn1 = forge.pki.privateKeyToAsn1(key);
      const privateKeyHex = forge.util.bytesToHex(asn1.value[1].value);

      // Detect curve type
      let algorithm = '11'; // Default to P-256
      let keySize = 32;

      if (privateKeyHex.length === 64) {
        algorithm = '11'; // P-256
        keySize = 32;
      } else if (privateKeyHex.length === 96) {
        algorithm = '14'; // P-384
        keySize = 48;
      }

      return {
        type: 'ECC',
        algorithm: algorithm,
        size: keySize * 8,
        curve: keySize === 32 ? 'P-256' : 'P-384',
        privateKey: privateKeyHex
      };
    }

    throw new Error('Unsupported key format');
  } catch (error) {
    console.error('Failed to parse PEM key:', error);
    throw new Error('Failed to parse private key: ' + error.message);
  }
}

/**
 * Parse DER format private key
 * @param {ArrayBuffer} derData - DER file data
 * @returns {Object} Parsed key data
 */
export async function parseDERPrivateKey(derData) {
  try {
    const derBytes = forge.util.createBuffer(derData);
    const asn1 = forge.asn1.fromDer(derBytes);

    // Try to parse as PKCS#8
    try {
      const key = forge.pki.privateKeyFromAsn1(asn1);

      if (key.n) {
        // RSA key
        const bitLength = key.n.bitLength();
        const byteLength = Math.ceil(bitLength / 8);

        return {
          type: 'RSA',
          algorithm: bitLength === 2048 ? '07' : bitLength === 3072 ? '05' : bitLength === 4096 ? '16' : '07',
          size: bitLength,
          n: bigIntToHex(key.n, byteLength),
          e: bigIntToHex(key.e, 4),
          p: bigIntToHex(key.p, byteLength / 2),
          q: bigIntToHex(key.q, byteLength / 2),
          dP: bigIntToHex(key.dP, byteLength / 2),
          dQ: bigIntToHex(key.dQ, byteLength / 2),
          qInv: bigIntToHex(key.qInv, byteLength / 2)
        };
      }
    } catch (e) {
      // Not PKCS#8, might be raw key
    }

    throw new Error('Unsupported DER format');
  } catch (error) {
    console.error('Failed to parse DER key:', error);
    throw new Error('Failed to parse DER key: ' + error.message);
  }
}

/**
 * Build PIV import APDU command
 * @param {Object} keyData - Parsed key data
 * @param {string} slot - PIV slot (9A, 9C, 9D, 9E)
 * @param {Object} options - Additional options (pinPolicy, touchPolicy)
 * @returns {string} APDU command in hex
 */
export function buildPIVImportAPDU(keyData, slot, options = {}) {
  const tlvData = [];

  if (keyData.type === 'RSA') {
    // Build RSA TLV structure
    // Tag 01: p
    tlvData.push(0x01);
    const pBytes = hexToBytes(keyData.p);
    tlvData.push(pBytes.length);
    tlvData.push(...pBytes);

    // Tag 02: q
    tlvData.push(0x02);
    const qBytes = hexToBytes(keyData.q);
    tlvData.push(qBytes.length);
    tlvData.push(...qBytes);

    // Tag 03: dP
    tlvData.push(0x03);
    const dpBytes = hexToBytes(keyData.dP);
    tlvData.push(dpBytes.length);
    tlvData.push(...dpBytes);

    // Tag 04: dQ
    tlvData.push(0x04);
    const dqBytes = hexToBytes(keyData.dQ);
    tlvData.push(dqBytes.length);
    tlvData.push(...dqBytes);

    // Tag 05: qInv
    tlvData.push(0x05);
    const qinvBytes = hexToBytes(keyData.qInv);
    tlvData.push(qinvBytes.length);
    tlvData.push(...qinvBytes);
  } else if (keyData.type === 'ECC') {
    // Build ECC TLV structure
    // Tag 06: private key
    tlvData.push(0x06);
    const priBytes = hexToBytes(keyData.privateKey);
    tlvData.push(priBytes.length);
    tlvData.push(...priBytes);
  }

  // Add PIN policy if specified
  if (options.pinPolicy !== undefined) {
    tlvData.push(0xAA);
    tlvData.push(0x01);
    tlvData.push(options.pinPolicy);
  }

  // Add touch policy if specified
  if (options.touchPolicy !== undefined) {
    tlvData.push(0xAB);
    tlvData.push(0x01);
    tlvData.push(options.touchPolicy);
  }

  // Build complete APDU
  const apdu = [
    0x00,                    // CLA
    0xFE,                    // INS (IMPORT_ASYMMETRIC_KEY)
    parseInt(keyData.algorithm, 16), // P1 (algorithm)
    parseInt(slot, 16),      // P2 (slot)
    tlvData.length,          // LC
    ...tlvData               // DATA
  ];

  return bytesToHex(apdu);
}

/**
 * Build OpenPGP import APDU command
 * @param {Object} keyData - Parsed key data
 * @param {string} keyType - Key type ('sig', 'dec', 'aut')
 * @returns {string} APDU command in hex
 */
export function buildOpenPGPImportAPDU(keyData, keyType) {
  const keyRef = keyType === 'sig' ? 0xB6 : keyType === 'dec' ? 0xB8 : 0xA4;

  const templateData = [];
  const concatenatedData = [];

  if (keyData.type === 'RSA') {
    // 7F48 template
    // 91: e
    templateData.push(0x91, 0x03, 0x01, 0x00, 0x01); // e = 65537

    // 92: p
    templateData.push(0x92);
    const pBytes = hexToBytes(keyData.p);
    templateData.push(pBytes.length);

    // 93: q
    templateData.push(0x93);
    const qBytes = hexToBytes(keyData.q);
    templateData.push(qBytes.length);

    // 94: qinv
    templateData.push(0x94);
    const qinvBytes = hexToBytes(keyData.qInv);
    templateData.push(qinvBytes.length);

    // 95: dp
    templateData.push(0x95);
    const dpBytes = hexToBytes(keyData.dP);
    templateData.push(dpBytes.length);

    // 96: dq
    templateData.push(0x96);
    const dqBytes = hexToBytes(keyData.dQ);
    templateData.push(dqBytes.length);

    // 5F48: concatenated data
    concatenatedData.push(...pBytes, ...qBytes, ...qinvBytes, ...dpBytes, ...dqBytes);
  } else if (keyData.type === 'ECC') {
    // 7F48 template
    // 92: private key length
    templateData.push(0x92);
    const priBytes = hexToBytes(keyData.privateKey);
    templateData.push(priBytes.length);

    // 5F48: private key data
    concatenatedData.push(...priBytes);
  }

  // Build 4D structure
  const extendedHeader = [
    keyRef, 0x00  // Key reference
  ];

  const template7F48 = [
    0x7F, 0x48,
    templateData.length,
    ...templateData
  ];

  const data5F48 = [
    0x5F, 0x48,
    concatenatedData.length,
    ...concatenatedData
  ];

  const fullData = [
    0x4D,
    extendedHeader.length + template7F48.length + data5F48.length,
    ...extendedHeader,
    ...template7F48,
    ...data5F48
  ];

  // Build APDU
  const apdu = [
    0x00,           // CLA
    0xDB,           // INS (IMPORT_KEY)
    0x3F,           // P1
    0xFF,           // P2
    fullData.length, // LC
    ...fullData     // DATA
  ];

  return bytesToHex(apdu);
}

/**
 * Detect key algorithm from parsed key data
 * @param {Object} keyData - Parsed key data
 * @returns {string} Algorithm ID
 */
export function detectKeyAlgorithm(keyData) {
  if (keyData.type === 'RSA') {
    if (keyData.size === 2048) return '07';
    if (keyData.size === 3072) return '05';
    if (keyData.size === 4096) return '16';
  } else if (keyData.type === 'ECC') {
    if (keyData.curve === 'P-256') return '11';
    if (keyData.curve === 'P-384') return '14';
  }
  return '07'; // Default to RSA-2048
}

/**
 * Validate key data
 * @param {Object} keyData - Parsed key data
 * @returns {Object} Validation result
 */
export function validateKeyData(keyData) {
  if (!keyData || !keyData.type) {
    return { valid: false, error: 'Invalid key data' };
  }

  if (keyData.type === 'RSA') {
    if (!keyData.p || !keyData.q || !keyData.dP || !keyData.dQ || !keyData.qInv) {
      return { valid: false, error: 'Missing RSA key components' };
    }

    if (keyData.size < 2048) {
      return { valid: false, error: 'RSA key size must be at least 2048 bits' };
    }

    if (keyData.size > 4096) {
      return { valid: false, error: 'RSA key size exceeds maximum (4096 bits)' };
    }
  } else if (keyData.type === 'ECC') {
    if (!keyData.privateKey) {
      return { valid: false, error: 'Missing ECC private key' };
    }

    const keyLength = keyData.privateKey.length / 2;
    if (keyLength !== 32 && keyLength !== 48) {
      return { valid: false, error: 'Invalid ECC key length' };
    }
  }

  return { valid: true };
}
